import {getAccessToken} from "./tokenManager";
import {getBoundFetch} from "./getBoundFetch";

const EVENTS_URL = "https://api.neuronsearchlab.com/v1/events";

/**
 * Post events straight to /v1/events rather than through the SDK.
 *
 * The SDK's trackEvent normalises `type` to a *string* before sending, and the
 * current API rejects that outright ("Expected number, received string"), so
 * every event the demo sent was failing with HTTP 400. It also never maps
 * `eventId` to the `event_id` the API requires. Until the SDK catches up, build
 * the payload here.
 *
 * The contract, verified against production:
 *   user_id      string   required
 *   event_id     number   required, must exist in the tenant's Event config
 *   item_id      number   required, positive integer from NSL ingestion
 *   occurred_at  number   optional, epoch seconds
 *   metadata     object   optional
 *   request_id   string   optional, ties the event to the ranking that served it
 * A bare object or an array of them is accepted; `type` must be omitted or numeric.
 */

const isNumericId = (value) =>
  (typeof value === "number" && Number.isFinite(value) && value > 0) ||
  (typeof value === "string" && /^\d+$/.test(value.trim()));

const toNumericId = (value) =>
  typeof value === "number" ? value : Number(String(value).trim());

/**
 * Items ingested before the platform's uuid -> integer migration are addressed
 * by uuid, which /v1/events cannot accept. Those stories are not in the
 * catalogue at all, so drop the event rather than send a request that 400s.
 */
export const canTrackNeuronItem = (itemId) => isNumericId(itemId);

export const trackNeuronEvent = async ({
  eventId,
  itemId,
  userId,
  metadata,
  requestId,
  occurredAt,
}) => {
  if (!isNumericId(eventId)) {
    throw new Error("eventId must be a numeric NSL event id");
  }
  if (!isNumericId(itemId)) {
    // Not an error worth throwing at call sites - the story simply predates the
    // id migration and has no NSL counterpart.
    return null;
  }
  if (typeof userId !== "string" && typeof userId !== "number") {
    throw new Error("userId must be a string or number");
  }

  const payload = {
    user_id: String(userId),
    event_id: toNumericId(eventId),
    item_id: toNumericId(itemId),
    occurred_at:
      typeof occurredAt === "number"
        ? occurredAt
        : Math.floor(Date.now() / 1000),
  };
  if (metadata && typeof metadata === "object") payload.metadata = metadata;
  if (typeof requestId === "string" && requestId) payload.request_id = requestId;

  const token = await getAccessToken();
  const fetchImpl = getBoundFetch() || fetch;

  const response = await fetchImpl(EVENTS_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(`Failed to track event (${response.status}): ${text}`);
  }

  return response.json().catch(() => null);
};

export default trackNeuronEvent;
